(() => {
  if (customElements.get('cn-hero-carousel')) return;
  const DEFAULT_INTERVAL = 5000;
  const TRANSITION = 650;
  class CnHeroCarousel extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.slides = [...this.querySelectorAll('.cn-hero-slide')];
      this.controls = this.querySelector('[data-cn-carousel-controls]');
      this.track = this.querySelector('.cn-hero-track');
      if (!this.slides.length || !this.controls) return;
      this.headings = this.slides.map(slide => slide.querySelector('[data-cn-hero-heading]'));
      this.dots = [...this.querySelectorAll('[data-cn-slide-to]')];
      this.progress = this.querySelector('.cn-hero-progress > span');
      this.slideNumber = this.querySelector('[data-cn-slide-number]');
      this.status = this.querySelector('[data-cn-carousel-status]');
      this.toggle = this.querySelector('.cn-hero-toggle');
      this.pauseIcon = this.querySelector('[data-cn-pause-icon]');
      this.playIcon = this.querySelector('[data-cn-play-icon]');
      this.index = Math.max(0, this.slides.findIndex(slide => slide.classList.contains('is-active')));
      const configuredInterval = Number(this.dataset.interval);
      this.interval = Number.isFinite(configuredInterval) && configuredInterval > 0 ? Math.min(10000, Math.max(3000, configuredInterval)) : DEFAULT_INTERVAL;
      this.remaining = this.interval;
      this.media = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.userPaused = this.media.matches || this.dataset.autoplay === 'false';
      this.hovered = false;
      this.focused = this.track.contains(document.activeElement);
      if (this.contains(document.activeElement)) this.userPaused = true;
      this.controller = new AbortController();
      const listen = (target, type, listener) => target.addEventListener(type, listener, { signal: this.controller.signal });
      this.classList.add('is-ready');
      this.controls.hidden = this.slides.length < 2;
      this.setSlideState();
      if (this.slides.length < 2) return;
      listen(this.querySelector('.cn-hero-prev'), 'click', () => this.go(this.index - 1, 'previous', true));
      listen(this.querySelector('.cn-hero-next'), 'click', () => this.go(this.index + 1, 'next', true));
      const toggle = this.toggle;
      listen(toggle, 'pointerdown', () => { this.pointerPauseIntent = !this.userPaused; });
      listen(toggle, 'pointercancel', () => { this.pointerPauseIntent = null; });
      listen(toggle, 'click', event => {
        // A pointer click focuses this button before click. Preserve the action
        // the visitor chose before focus itself stopped automatic rotation.
        this.userPaused = event.detail > 0 && typeof this.pointerPauseIntent === 'boolean' ? this.pointerPauseIntent : !this.userPaused;
        this.pointerPauseIntent = null;
        this.syncPlayback();
      });
      this.dots.forEach(button => listen(button, 'click', () => {
        const next = Number(button.dataset.cnSlideTo);
        this.go(next, next < this.index ? 'previous' : 'next', true);
      }));
      listen(this, 'pointerenter', event => { if (event.pointerType !== 'touch') { this.hovered = true; this.syncPlayback(); } });
      listen(this, 'pointerleave', event => { if (event.pointerType !== 'touch') { this.hovered = false; this.syncPlayback(); } });
      listen(this, 'focusin', event => {
        this.focused = this.track.contains(event.target);
        // Focus anywhere in the carousel stops rotation until deliberate Play.
        // Moving between arrow/dot controls must never silently restart it.
        this.userPaused = true;
        this.syncPlayback();
      });
      listen(this, 'focusout', () => {
        clearTimeout(this.focusTimer);
        this.focusTimer = setTimeout(() => { this.focused = this.track.contains(document.activeElement); this.syncPlayback(); }, 0);
      });
      listen(this, 'keydown', event => {
        if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey || !['ArrowRight', 'ArrowLeft'].includes(event.key) || event.target.closest('input,select,textarea')) return;
        event.preventDefault();
        this.go(this.index + (event.key === 'ArrowRight' ? 1 : -1), event.key === 'ArrowRight' ? 'next' : 'previous', true);
      });
      listen(this.track, 'touchstart', event => {
        this.touch = event.touches.length === 1 ? { x: event.touches[0].clientX, y: event.touches[0].clientY } : null;
        this.touching = Boolean(this.touch);
        this.syncPlayback();
      });
      listen(this.track, 'touchend', event => {
        const start = this.touch; this.touch = null;
        this.touching = false;
        this.syncPlayback();
        if (!start || !event.changedTouches.length) return;
        const dx = event.changedTouches[0].clientX - start.x;
        const dy = event.changedTouches[0].clientY - start.y;
        if (Math.abs(dx) < 50 || Math.abs(dx) < Math.abs(dy) * 1.3) return;
        this.suppressClickUntil = performance.now() + 600;
        this.go(this.index + (dx < 0 ? 1 : -1), dx < 0 ? 'next' : 'previous', true);
      });
      listen(this.track, 'touchcancel', () => { this.touch = null; this.touching = false; this.syncPlayback(); });
      this.addEventListener('click', event => {
        if (event.detail === 0 || performance.now() >= (this.suppressClickUntil || 0)) return;
        this.suppressClickUntil = 0;
        event.preventDefault();
        event.stopPropagation();
      }, { capture: true, signal: this.controller.signal });
      listen(document, 'visibilitychange', () => this.syncPlayback());
      listen(this.media, 'change', event => { if (event.matches) this.userPaused = true; this.syncPlayback(); });
      listen(document, 'shopify:block:select', event => {
        if (event.detail?.sectionId && String(event.detail.sectionId) !== this.dataset.sectionId) return;
        const selected = this.slides.findIndex(slide => slide.dataset.blockId === event.detail?.blockId);
        if (selected < 0) return;
        this.userPaused = true;
        this.go(selected, selected < this.index ? 'previous' : 'next', true);
        this.syncPlayback();
      });
      listen(document, 'shopify:section:unload', event => {
        if (String(event.detail?.sectionId) === this.dataset.sectionId) this.disconnectedCallback();
      });
      this.syncPlayback();
    }
    disconnectedCallback() {
      this.stopTimer();
      clearTimeout(this.animationTimer);
      clearTimeout(this.focusTimer);
      this.controller?.abort();
      this.controller = null;
      this.dataset.playing = 'false';
      this.classList.remove('is-animated');
      this.exitingSlide = null;
      if (this.slides?.length) this.setSlideState();
    }
    setSlideState(previousIndex) {
      const indices = Number.isInteger(previousIndex) ? [...new Set([previousIndex, this.index])] : this.slides.map((_, index) => index);
      indices.forEach(index => {
        const slide = this.slides[index];
        const active = index === this.index;
        slide.classList.toggle('is-active', active);
        slide.classList.remove('is-exiting');
        slide.hidden = !active;
        slide.inert = !active;
        slide.toggleAttribute('inert', !active);
        slide.setAttribute('aria-hidden', String(!active));
        // A selected slide must load immediately, even when lazy loading deferred it.
        // Keep unselected images browser-managed so they do not all compete with the first paint.
        if (active) slide.querySelectorAll('img[loading="lazy"]').forEach(image => image.setAttribute('loading', 'eager'));
        const heading = this.headings[index];
        const headingTag = active ? 'H1' : 'H2';
        if (heading && heading.tagName !== headingTag) {
          const replacement = document.createElement(headingTag.toLowerCase());
          for (const attribute of heading.attributes) replacement.setAttribute(attribute.name, attribute.value);
          replacement.append(...heading.childNodes);
          heading.replaceWith(replacement);
          this.headings[index] = replacement;
        }
      });
      this.dots.forEach(button => {
        const index = Number(button.dataset.cnSlideTo);
        if (!Number.isInteger(previousIndex) || index === previousIndex || index === this.index) button.setAttribute('aria-current', String(index === this.index));
      });
      const activeDot = this.dots.find(button => Number(button.dataset.cnSlideTo) === this.index);
      const dotList = activeDot?.parentElement;
      if (dotList && dotList.scrollWidth > dotList.clientWidth) {
        const left = activeDot.offsetLeft, right = left + activeDot.offsetWidth;
        if (left < dotList.scrollLeft) dotList.scrollLeft = left;
        else if (right > dotList.scrollLeft + dotList.clientWidth) dotList.scrollLeft = right - dotList.clientWidth;
      }
      const number = String(this.index + 1).padStart(2, '0');
      if (this.slideNumber.textContent !== number) this.slideNumber.textContent = number;
    }
    go(target, direction = 'next', manual = false) {
      const next = (target + this.slides.length) % this.slides.length;
      this.stopTimer();
      this.remaining = this.interval;
      this.updateProgress();
      if (next !== this.index) {
        const moveFocus = manual && this.slides[this.index].contains(document.activeElement);
        clearTimeout(this.animationTimer);
        this.classList.remove('is-animated');
        if (this.exitingSlide) {
          this.exitingSlide.classList.remove('is-exiting');
          this.exitingSlide.hidden = true;
          this.exitingSlide = null;
        }
        const previousIndex = this.index;
        const previous = this.slides[this.index];
        this.index = next;
        this.setSlideState(previousIndex);
        this.dataset.direction = direction;
        if (moveFocus) this.dots.find(button => Number(button.dataset.cnSlideTo) === next).focus({ preventScroll: true });
        if (!this.media.matches) {
          previous.hidden = false;
          previous.classList.add('is-exiting');
          this.exitingSlide = previous;
          void this.offsetWidth;
          this.classList.add('is-animated');
          this.animationTimer = setTimeout(() => {
            this.classList.remove('is-animated');
            previous.classList.remove('is-exiting');
            previous.hidden = true;
            if (this.exitingSlide === previous) this.exitingSlide = null;
          }, TRANSITION);
        }
        if (manual) {
          const title = this.slides[this.index].dataset.slideTitle;
          this.status.textContent = `Folie ${this.index + 1} von ${this.slides.length}${title ? `: ${title}` : ''}`;
        }
      }
      this.syncPlayback();
    }
    stopTimer() {
      if (this.startedAt != null) this.remaining = Math.max(0, this.remaining - (performance.now() - this.startedAt));
      this.startedAt = null;
      clearTimeout(this.advanceTimer);
      clearInterval(this.progressTimer);
      this.advanceTimer = null;
      this.progressTimer = null;
    }
    updateProgress() {
      const elapsed = this.startedAt == null ? 0 : performance.now() - this.startedAt;
      const progress = Math.min(1, Math.max(0, (this.interval - this.remaining + elapsed) / this.interval));
      this.progress.style.transform = `scaleX(${progress})`;
    }
    syncPlayback() {
      this.stopTimer();
      const paused = this.userPaused || this.hovered || this.focused || this.touching || document.hidden || this.slides.length < 2 || !this.isConnected;
      this.dataset.playing = String(!paused);
      const toggle = this.toggle;
      toggle.setAttribute('aria-label', this.userPaused ? 'Automatischen Wechsel starten' : 'Automatischen Wechsel pausieren');
      toggle.removeAttribute('aria-pressed');
      this.pauseIcon.toggleAttribute('hidden', this.userPaused);
      this.playIcon.toggleAttribute('hidden', !this.userPaused);
      this.updateProgress();
      if (paused) return;
      this.startedAt = performance.now();
      this.advanceTimer = setTimeout(() => this.go(this.index + 1, 'next'), this.remaining);
      this.progressTimer = setInterval(() => this.updateProgress(), 50);
    }
  }
  customElements.define('cn-hero-carousel', CnHeroCarousel);
})();
